/**
 * LocationSummaryMobileCards
 * Mobile card view for Location Summary tab
 * Displays location dimension data with selected metrics
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  useWindowDimensions,
  Platform,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { FontFamily } from '../../../../../constants/fonts';
import FieldSelector, { FieldDefinition } from './FieldSelector';
import { useTheme } from '../../../../../theme/useTheme';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface LocationSummaryMobileRow {
  dimensions: Record<string, string>;
  /** Resolved location ID (when known) — used to drive navigation filter selections. */
  locationId?: string;
  planned: number;
  completed: number;
  incomplete: number;
  onTime: number;
  daeN: number;
  daeD: number;
  completedPct: number;
  onTimePct: number;
  daePct: number;
}

export interface LocationSummaryMobileCardsProps {
  rows: LocationSummaryMobileRow[];
  loading?: boolean;
  error?: string | null;
  adminLocations?: any[];
  /**
   * When true, overrides dark mode regardless of the internal theme check.
   * Used when a parent component has already computed isDark for the mobile context.
   */
  isDark?: boolean;
  /**
   * Called when a Region/Unit, Market/POD, or Location value is pressed.
   * Caller is expected to switch to Executive Overview and apply the
   * corresponding filter while preserving all other applied filters.
   */
  onEntityPress?: (
    filterType: "region" | "market" | "location",
    item: { id: string; displayText: string }
  ) => void;
  /**
   * Called when an Incomplete value (> 0) is pressed. Caller is expected to
   * switch to Load Execution with Status set to Incomplete. When `location`
   * is provided (card is at Location grain), the caller should also apply
   * that Location filter.
   */
  onIncompleteLoadsPress?: (location?: { id: string; displayText: string }) => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const AVAILABLE_LOCATION_FIELDS: FieldDefinition[] = [
  { id: 'region', label: 'Region / Unit', shortLabel: 'Region' },
  { id: 'market', label: 'Market / POD', shortLabel: 'Market' },
  { id: 'location', label: 'Location', shortLabel: 'Loc' },
  { id: 'locationType', label: 'Location Type', shortLabel: 'Type' },
  { id: 'year', label: 'Year', shortLabel: 'Yr' },
  { id: 'period', label: 'Period', shortLabel: 'Per' },
  { id: 'week', label: 'Week in Year', shortLabel: 'Wk' },
  { id: 'deliveryDate', label: 'Delivery Date', shortLabel: 'Date' },
];

const DEFAULT_LOCATION_FIELDS = ['region', 'market', 'location'];

const normalizeMobileFilterValue = (value: unknown): string =>
  String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

const METRICS_FIELDS: FieldDefinition[] = [
  { id: 'planned', label: 'Planned Loads' },
  { id: 'completed', label: 'Completed Loads' },
  { id: 'incomplete', label: 'Incomplete Loads' },
  { id: 'completedPct', label: 'Completed %' },
  { id: 'onTime', label: 'On-Time Loads' },
  { id: 'onTimePct', label: 'On-Time %' },
  { id: 'daeN', label: 'Driver Arrived Count' },
  { id: 'daePct', label: 'Driver Arrived %' },
];

// ─── Component ────────────────────────────────────────────────────────────────

const LocationSummaryMobileCards: React.FC<LocationSummaryMobileCardsProps> = ({
  rows,
  loading = false,
  error = null,
  adminLocations = [],
  isDark: isDarkProp,
  onEntityPress,
  onIncompleteLoadsPress,
}) => {
  const { width } = useWindowDimensions();
  const { resolvedTheme, colors } = useTheme();
  const isMobilePhone = Platform.OS === 'ios' && width < 768;
  // isDark is true when prop says so (parent overrides), OR internal phone+dark detection
  const isDark = isDarkProp ?? (isMobilePhone && resolvedTheme === 'dark');

  const [isFieldSelectorExpanded, setIsFieldSelectorExpanded] = useState(false);
  const [selectedDimensionFields, setSelectedDimensionFields] = useState<string[]>(
    DEFAULT_LOCATION_FIELDS
  );
  const [appliedDimensionFields, setAppliedDimensionFields] = useState<string[]>(
    DEFAULT_LOCATION_FIELDS
  );

  // ─── Handlers ─────────────────────────────────────────────────────

  const handleToggleFieldSelector = useCallback(() => {
    setIsFieldSelectorExpanded(prev => !prev);
  }, []);

  const handleFieldToggle = useCallback((fieldId: string) => {
    setSelectedDimensionFields(prev => {
      if (prev.includes(fieldId)) {
        return prev.filter(id => id !== fieldId);
      }
      if (prev.length >= 3) {
        return prev;
      }
      return [...prev, fieldId];
    });
  }, []);

  const handleReset = useCallback(() => {
    setSelectedDimensionFields(DEFAULT_LOCATION_FIELDS);
  }, []);

  const handleApply = useCallback(() => {
    setAppliedDimensionFields(selectedDimensionFields);
    setIsFieldSelectorExpanded(false);
  }, [selectedDimensionFields]);

  // Region/Market/Location value press → Executive Overview with that single
  // entity selected as the corresponding filter (parent preserves everything else).
  const handleEntityPress = useCallback(
    (dimensionKey: 'region' | 'market' | 'location', row: LocationSummaryMobileRow) => {
      if (!onEntityPress) return;
      const displayText = row.dimensions[dimensionKey];
      if (!displayText) return;
      const id =
        dimensionKey === 'market'
          ? normalizeMobileFilterValue(displayText)
          : dimensionKey === 'location'
          ? row.locationId || displayText
          : displayText;
      onEntityPress(dimensionKey, { id, displayText });
    },
    [onEntityPress]
  );

  // Incomplete value press (only rendered as a link when count > 0) → Load
  // Execution with Status = Incomplete, also selecting the row's Location
  // when the card is currently grouped at Location grain.
  //
  // Restricted to Desktop (web) only — on native mobile and iPad this must be
  // a complete no-op (no tab switch, no filter/state change, no API calls).
  // Note: this component only renders on native mobile phones (see
  // isMobilePhone gating in LocationSummaryTab), so Platform.OS here is
  // always 'ios'/'android', never 'web' — this guard always short-circuits
  // in practice, but is kept explicit/defensive rather than assumed.
  const handleIncompletePress = useCallback(
    (row: LocationSummaryMobileRow) => {
      if (Platform.OS !== 'web') return;
      if (!onIncompleteLoadsPress || row.incomplete <= 0) return;
      const isLocationGrain = appliedDimensionFields.includes('location');
      if (isLocationGrain && row.dimensions.location) {
        onIncompleteLoadsPress({
          id: row.locationId || row.dimensions.location,
          displayText: row.dimensions.location,
        });
      } else {
        onIncompleteLoadsPress(undefined);
      }
    },
    [onIncompleteLoadsPress, appliedDimensionFields]
  );

  // ─── Render Helpers ───────────────────────────────────────────────

  const renderCard = (row: LocationSummaryMobileRow, index: number) => {
    const titleDimension = row.dimensions['region'] || 'Unknown';
    // On-time % colour — semantic meaning preserved: blue = on target, red = below
    const onTimePctColor = row.onTimePct >= 98.5
      ? (isDark ? colors.brand.primary : '#005CBC')
      : (isDark ? '#F87171' : '#C2410C');

    return (
      <View
        key={`card-${index}`}
        style={[
          styles.card,
          isDark && {
            backgroundColor: colors.background.surface,
            borderColor: colors.border.default,
          },
        ]}
      >
        {/* Card Title - Region/Unit (fixed, not selectable) */}
        <View
          style={[
            styles.cardHeader,
            isDark && {
              backgroundColor: colors.background.elevated,
              borderBottomColor: colors.border.divider,
            },
          ]}
        >
          <Text
            style={[styles.cardTitle, isDark && { color: colors.text.primary }]}
            numberOfLines={2}
            onPress={onEntityPress ? () => handleEntityPress('region', row) : undefined}
            suppressHighlighting={!onEntityPress}
          >
            {titleDimension}
          </Text>
          <Text style={[styles.cardSubtitle, isDark && { color: colors.text.secondary }]}>
            Region / Unit
          </Text>
        </View>

        {/* Selected Fields */}
        <View style={styles.cardContent}>
          {appliedDimensionFields.map((fieldId) => {
            const value = row.dimensions[fieldId];
            const field = AVAILABLE_LOCATION_FIELDS.find(f => f.id === fieldId);
            const isLinkableDimension =
              (fieldId === 'region' || fieldId === 'market' || fieldId === 'location') &&
              !!onEntityPress &&
              !!value;
            return (
              <View key={`${index}-${fieldId}`} style={styles.fieldRow}>
                <Text style={[styles.fieldLabel, isDark && { color: colors.text.secondary }]}>
                  {field?.shortLabel || field?.label}
                </Text>
                {isLinkableDimension ? (
                  <TouchableOpacity
                    onPress={() =>
                      handleEntityPress(fieldId as 'region' | 'market' | 'location', row)
                    }
                    activeOpacity={0.7}
                  >
                    <Text
                      style={[
                        styles.fieldValue,
                        styles.linkText,
                        isDark && { color: colors.brand.primary },
                      ]}
                      numberOfLines={1}
                    >
                      {value}
                    </Text>
                  </TouchableOpacity>
                ) : (
                  <Text
                    style={[styles.fieldValue, isDark && { color: colors.text.primary }]}
                    numberOfLines={1}
                  >
                    {value || '-'}
                  </Text>
                )}
              </View>
            );
          })}
        </View>

        {/* Metrics Footer */}
        <View
          style={[
            styles.cardFooter,
            isDark && {
              backgroundColor: colors.background.elevated,
              borderTopColor: colors.border.divider,
            },
          ]}
        >
          <View style={styles.metricItem}>
            <Text style={[styles.metricValue, isDark && { color: colors.text.primary }]}>
              {row.completed}
            </Text>
            <Text style={[styles.metricLabel, isDark && { color: colors.text.secondary }]}>
              Complete
            </Text>
          </View>
          <View style={[styles.metricDivider, isDark && { backgroundColor: colors.border.divider }]} />
          <View style={styles.metricItem}>
            {row.incomplete > 0 && onIncompleteLoadsPress ? (
              <TouchableOpacity onPress={() => handleIncompletePress(row)} activeOpacity={0.7}>
                <Text style={[styles.metricValue, styles.linkText, isDark && { color: colors.brand.primary }]}>
                  {row.incomplete}
                </Text>
              </TouchableOpacity>
            ) : (
              <Text style={[styles.metricValue, isDark && { color: colors.text.primary }]}>
                {row.incomplete}
              </Text>
            )}
            <Text style={[styles.metricLabel, isDark && { color: colors.text.secondary }]}>
              Incomplete
            </Text>
          </View>
          <View style={[styles.metricDivider, isDark && { backgroundColor: colors.border.divider }]} />
          <View style={styles.metricItem}>
            <Text style={[styles.metricValue, { color: onTimePctColor }]}>
              {row.onTimePct.toFixed(1)}%
            </Text>
            <Text style={[styles.metricLabel, isDark && { color: colors.text.secondary }]}>
              On-Time
            </Text>
          </View>
        </View>
      </View>
    );
  };

  if (loading) {
    return (
      <View
        style={[
          styles.centerContainer,
          isDark && {
            backgroundColor: colors.background.surface,
            borderColor: colors.border.default,
          },
        ]}
      >
        <ActivityIndicator size="large" color={isDark ? colors.brand.primary : '#005CBC'} />
        <Text style={[styles.loadingText, isDark && { color: colors.text.secondary }]}>
          Loading location summary...
        </Text>
      </View>
    );
  }

  if (error) {
    return (
      <View
        style={[
          styles.centerContainer,
          isDark && {
            backgroundColor: colors.background.surface,
            borderColor: colors.border.default,
          },
        ]}
      >
        <MaterialIcons name="error-outline" size={48} color="#C2410C" />
        <Text style={[styles.errorText, isDark && { color: '#F87171' }]}>{error}</Text>
      </View>
    );
  }

  if (rows.length === 0) {
    return (
      <View
        style={[
          styles.centerContainer,
          isDark && {
            backgroundColor: colors.background.surface,
            borderColor: colors.border.default,
          },
        ]}
      >
        <MaterialIcons
          name="info-outline"
          size={48}
          color={isDark ? colors.text.disabled : '#ADB5BD'}
        />
        <Text style={[styles.emptyText, isDark && { color: colors.text.secondary }]}>
          No records match the current filters
        </Text>
      </View>
    );
  }

  return (
    <ScrollView
      style={[
        styles.container,
        isDark && { backgroundColor: colors.background.page },
      ]}
      contentContainerStyle={styles.scrollContent}
      showsVerticalScrollIndicator={false}
    >
      {/* Field Selector */}
      <FieldSelector
        isExpanded={isFieldSelectorExpanded}
        selectedFields={selectedDimensionFields}
        availableFields={AVAILABLE_LOCATION_FIELDS}
        maxFields={3}
        onToggle={handleToggleFieldSelector}
        onFieldToggle={handleFieldToggle}
        onReset={handleReset}
        onApply={handleApply}
        title="Location Fields"
        isDark={isDark}
      />

      {/* Cards Grid */}
      <View style={styles.cardsContainer}>
        {rows.map((row, index) => renderCard(row, index))}
      </View>
    </ScrollView>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  scrollContent: {
    paddingHorizontal: 14,
    paddingTop: 1,
    paddingBottom: 12,
  },
  centerContainer: {
    paddingVertical: 40,
    paddingHorizontal: 20,
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    marginHorizontal: 14,
    marginTop: 12,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    fontFamily: FontFamily.regular,
    color: '#6C757D',
  },
  errorText: {
    marginTop: 12,
    fontSize: 14,
    fontFamily: FontFamily.regular,
    color: '#C2410C',
    textAlign: 'center',
  },
  emptyText: {
    marginTop: 12,
    fontSize: 14,
    fontFamily: FontFamily.regular,
    color: '#6C757D',
  },
  cardsContainer: {
    gap: 12,
    marginTop: 12,
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    overflow: 'hidden',
    shadowColor: '#A8B4C5',
    shadowOpacity: 0.08,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },
  cardHeader: {
    paddingHorizontal: 14,
    paddingVertical: 12,
    backgroundColor: '#F9FAFB',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  cardTitle: {
    fontSize: 15,
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 4,
  },
  cardSubtitle: {
    fontSize: 12,
    fontFamily: FontFamily.regular,
    color: '#6B7280',
  },
  cardContent: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    gap: 8,
  },
  fieldRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 4,
  },
  fieldLabel: {
    fontSize: 11,
    fontFamily: FontFamily.bold,
    color: '#667085',
    textTransform: 'uppercase',
    letterSpacing: 0.2,
  },
  fieldValue: {
    fontSize: 12,
    fontFamily: FontFamily.medium,
    color: '#111827',
    flex: 1,
    marginLeft: 8,
    textAlign: 'right',
  },
  cardFooter: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 12,
    backgroundColor: '#F9FAFB',
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
  },
  metricItem: {
    alignItems: 'center',
    flex: 1,
  },
  metricDivider: {
    width: 1,
    height: 24,
    backgroundColor: '#E5E7EB',
  },
  metricValue: {
    fontSize: 13,
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    color: '#111827',
  },
  metricLabel: {
    fontSize: 10,
    fontFamily: FontFamily.regular,
    color: '#6B7280',
    marginTop: 2,
  },
  linkText: {
    color: '#0B5FC6',
    textDecorationLine: 'underline',
  },
});

LocationSummaryMobileCards.displayName = 'LocationSummaryMobileCards';

export default React.memo(LocationSummaryMobileCards);
